import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { emailSchema, passwordSchema, pinSchema } from '@tailoring-crm/shared';
import { AuthLayout } from '../components/auth/auth-layout.js';
import { FormError, PasswordField, EmailField } from '../components/auth/auth-fields.js';
import { OtpStep } from '../components/auth/otp-step.js';
import { PinPad } from '../components/auth/pin-pad.js';
import { Button, Input, Label } from '@tailoring-crm/ui';
import { useApiClient } from '../lib/api/api-context.js';
import type { OtpChallenge } from '../lib/auth/auth-contract.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { authErrorMessage } from '../lib/auth/auth-errors.js';
import { rememberDevice } from '../lib/auth/remembered-device.js';
import {
  completeJoin,
  startJoin,
  verifyJoin,
  type InvitePreview,
} from '../lib/staff/staff-join-api.js';

/**
 * Joining a shop you were invited to.
 *
 * The same three steps as signup and deliberately so — a karigar being set up at the counter is
 * standing next to the owner who invited them, and a flow that looks familiar to the owner is
 * one the owner can talk them through.
 *
 * What is NOT shown is as considered as what is. Until the code is verified this screen cannot
 * say which shop invited them, or whether the address was invited at all, because the API will
 * not tell it — otherwise anyone could type addresses into this page and learn where people work.
 * The shop's name appears on the third step, once the emailed code has been answered.
 *
 * A tailor sets a PIN on the keypad; a manager sets a password. That is not a preference, it is
 * how each of them will sign in from tomorrow, so the field they are given is the credential
 * their role actually uses. Someone who already works at another shop on this address skips it
 * entirely — they have one already, and the invitation does not change it.
 */
type Step = 'email' | 'code' | 'details';

export function StaffJoinPage(): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();
  const auth = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [verificationToken, setVerificationToken] = useState('');
  const [invite, setInvite] = useState<InvitePreview | null>(null);

  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [credentialError, setCredentialError] = useState<string | undefined>(undefined);

  const requestCode = async (address: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setChallenge(await startJoin(client, address));
      setStep('code');
    } catch (caught) {
      setError(authErrorMessage(caught, t));
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = (): void => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message);
      return;
    }
    setEmailError(undefined);
    setEmail(parsed.data);
    void requestCode(parsed.data);
  };

  const submitCode = (code: string): void => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const verified = await verifyJoin(client, { email, code });
        setVerificationToken(verified.verificationToken);
        setInvite(verified.invite);
        // The shop typed their name on the invitation; it is almost always right, and a person
        // being set up at a counter should not have to type it again.
        setName(verified.invite.name);
        setStep('details');
      } catch (caught) {
        setError(authErrorMessage(caught, t));
      } finally {
        setBusy(false);
      }
    })();
  };

  const resend = (): void => {
    setResending(true);
    void (async () => {
      try {
        setChallenge(await startJoin(client, email));
        setError(null);
      } catch (caught) {
        setError(authErrorMessage(caught, t));
      } finally {
        setResending(false);
      }
    })();
  };

  const submitDetails = (): void => {
    if (!invite) return;
    const needsCredential = !invite.hasCredential;

    if (needsCredential && invite.role === 'tailor' && !pinSchema.safeParse(pin).success) {
      setCredentialError(t('auth.pinInvalid'));
      return;
    }
    if (needsCredential && invite.role === 'manager') {
      const parsed = passwordSchema.safeParse(password);
      if (!parsed.success) {
        setCredentialError(parsed.error.issues[0]?.message);
        return;
      }
    }
    setCredentialError(undefined);

    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const session = await completeJoin(client, {
          verificationToken,
          name,
          ...(invite.role === 'tailor' && pin ? { pin } : {}),
          ...(invite.role === 'manager' && password ? { password } : {}),
        });
        rememberDevice({ email, method: invite.role === 'tailor' ? 'pin' : 'password' });
        auth.signIn({
          user: session.user,
          shop: session.shop,
          role: session.role,
          sessionId: session.sessionId,
          memberships: session.shop
            ? [{ tenantId: session.shop.id, name: session.shop.name, role: session.role }]
            : [],
        });
        // No navigation: `GuestOnly` sees the session and moves them into the shop.
      } catch (caught) {
        setError(authErrorMessage(caught, t));
        setBusy(false);
      }
    })();
  };

  const footer = (
    <span>
      {t('auth.haveAccount')}{' '}
      <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
        {t('auth.signIn')}
      </Link>
    </span>
  );

  if (step === 'code' && challenge) {
    return (
      <AuthLayout
        title={t('auth.verifyEmailTitle')}
        description={t('auth.verifyEmailDescription')}
        step={{ current: 2, total: 3 }}
        footer={footer}
      >
        <OtpStep
          challenge={challenge}
          submitting={busy}
          resending={resending}
          error={error}
          onSubmit={submitCode}
          onResend={resend}
          onChangeNumber={() => {
            setStep('email');
            setError(null);
          }}
        />
      </AuthLayout>
    );
  }

  if (step === 'details' && invite) {
    const needsCredential = !invite.hasCredential;
    return (
      <AuthLayout
        title={
          invite.shopName
            ? t('staff.join.detailsTitle', { shop: invite.shopName })
            : t('staff.join.detailsTitleFallback')
        }
        description={t('staff.join.detailsDescription', {
          role: t(`role.${invite.role}`).toLowerCase(),
        })}
        step={{ current: 3, total: 3 }}
        footer={footer}
      >
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            submitDetails();
          }}
          noValidate
        >
          <FormError message={error} />

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="join-name">{t('auth.yourName')}</Label>
            <Input
              id="join-name"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          {needsCredential && invite.role === 'tailor' ? (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">{t('staff.join.choosePin')}</p>
              <PinPad
                value={pin}
                onChange={setPin}
                label={t('staff.join.choosePin')}
                disabled={busy}
                invalid={credentialError !== undefined}
              />
              {credentialError ? (
                <p className="text-sm text-destructive">{credentialError}</p>
              ) : (
                <p className="text-sm text-muted-foreground">{t('staff.join.pinHint')}</p>
              )}
            </div>
          ) : null}

          {needsCredential && invite.role === 'manager' ? (
            <PasswordField
              label={t('auth.choosePassword')}
              hint={t('auth.passwordHint')}
              error={credentialError}
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          ) : null}

          {invite.hasCredential ? (
            <p className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground">
              {t('staff.join.existingCredential')}
            </p>
          ) : null}

          <Button type="submit" block disabled={busy}>
            {t('staff.join.submit')}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('staff.join.title')}
      description={t('staff.join.description')}
      step={{ current: 1, total: 3 }}
      footer={footer}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          submitEmail();
        }}
        noValidate
      >
        <FormError message={error} />

        <EmailField
          label={t('auth.emailAddress')}
          hint={t('staff.join.emailHint')}
          error={emailError}
          value={email}
          autoFocus
          onChange={(event) => setEmail(event.target.value)}
        />

        <Button type="submit" block disabled={busy}>
          {t('auth.sendCode')}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default StaffJoinPage;
